import React from 'react'

/**
 * Formats message text with proper mention styling
 * Converts <mention id="__id__">__display__</mention> tags to styled React elements
 * @param text - The raw message text with mention tags
 * @returns Array of strings and JSX elements for rendering
 */
export const formatMessageWithMentions = (text: string): (string | JSX.Element)[] => {
  if (!text) return [text]

  // Regular expression to find mentions in the format <mention id="__id__">__display__</mention>
  const mentionRegex = /<mention id="([^"]*)">([^<]*)<\/mention>/g

  // If no mentions, just return the text as is
  if (!text.includes('<mention')) {
    return [text]
  }

  // Split the text at mentions and create React elements
  let parts: (string | JSX.Element)[] = []
  let lastIndex = 0

  // Find all mentions and replace them
  text.replace(mentionRegex, (match, id, displayName, index) => {
    // Add text before mention
    if (index > lastIndex) {
      parts.push(text.substring(lastIndex, index))
    }

    // Add styled mention
    parts.push(
      <span
        key={`mention-${index}`}
        className="chat-mention"
        data-mention-id={id}
        style={{
          color: '#2563eb',
          fontWeight: 600,
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
          padding: '1px 3px',
          borderRadius: '3px',
          cursor: 'pointer',
          display: 'inline-block',
        }}
      >
        {displayName}
      </span>
    )

    lastIndex = index + match.length
    return match
  })

  // Add any remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex))
  }

  return parts
}

/**
 * Formats message text for plain text display (no JSX elements)
 * Converts <mention id="__id__">__display__</mention> tags to @displayName format
 * @param text - The raw message text with mention tags
 * @returns Clean text string with mentions formatted as @displayName
 */
export const formatMessageTextPlain = (text: string): string => {
  if (!text) return text

  // Regular expression to find mentions in the format <mention id="__id__">__display__</mention>
  const mentionRegex = /<mention id="([^"]*)">([^<]*)<\/mention>/g

  // If no mentions, just return the text as is
  if (!text.includes('<mention')) {
    return text
  }

  // Replace mentions with just the display name
  return text.replace(mentionRegex, (match, id, displayName) => {
    return `@${displayName}`
  })
}
